import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ShieldAlert, KeyRound, Eye, EyeOff, Trash2, Clock } from 'lucide-react';
import PageHeader from '@components/common/PageHeader';
import Button from '@components/Button/Button';
import Input from '@components/Form/Input/Input';
import masterPasswordService from '@/services/settings/masterPasswordService';

const MasterPasswordPage: React.FC = () => {
    const queryClient = useQueryClient();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const { data: status, isLoading } = useQuery({
        queryKey: ['master-password-status'],
        queryFn: () => masterPasswordService.getStatus(),
    });

    const { data: usageLog = [] } = useQuery({
        queryKey: ['master-password-usage-log'],
        queryFn: () => masterPasswordService.getUsageLog(),
        enabled: !!status?.is_set,
    });

    const reset = () => {
        setPassword('');
        setConfirmPassword('');
        setShowPassword(false);
    };

    const setMutation = useMutation({
        mutationFn: () => masterPasswordService.setPassword(password),
        onSuccess: () => {
            toast.success(status?.is_set ? 'Master password updated' : 'Master password set');
            reset();
            queryClient.invalidateQueries({ queryKey: ['master-password-status'] });
        },
        onError: (err: any) => toast.error(err?.response?.data?.message || 'Could not save the master password'),
    });

    const clearMutation = useMutation({
        mutationFn: () => masterPasswordService.clearPassword(),
        onSuccess: () => {
            toast.success('Master password cleared — student logins now require their own password only');
            queryClient.invalidateQueries({ queryKey: ['master-password-status'] });
        },
        onError: (err: any) => toast.error(err?.response?.data?.message || 'Could not clear the master password'),
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error('Master password must be at least 8 characters long');
            return;
        }
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        setMutation.mutate();
    };

    const handleClear = () => {
        if (!window.confirm('Clear the master password? Every student will then need their own password to log in.')) return;
        clearMutation.mutate();
    };

    return (
        <div className="flex flex-col gap-4 2xl:gap-5 !pt-0 p-3 md:p-4 2xl:p-5">
            <PageHeader
                title="Master Password"
                breadcrumbItems={[{ label: 'Dashboard', to: '/admin/dashboard' }, { label: 'Master Password' }]}
            />

            <div className="bg-[var(--white-color)] rounded-2xl border border-[var(--neutral-color)] p-4 sm:p-6 space-y-5 max-w-2xl">
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3.5">
                    <ShieldAlert size={18} className="text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 leading-relaxed">
                        Setting a master password lets anyone who knows it sign in as <strong>any student</strong>, on
                        the normal student login page, in place of that student's own password. Every use is
                        recorded in the log below. Only set this if you understand the risk — treat it like any
                        other admin credential.
                    </p>
                </div>

                <div className="flex items-center gap-2 text-sm">
                    <KeyRound size={16} className="text-[var(--subtitle-color)]" />
                    <span className="text-[var(--subtitle-color)]">Current status:</span>
                    {isLoading ? (
                        <span className="text-[var(--subtitle-color)]">Loading…</span>
                    ) : status?.is_set ? (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
                            Set{status.updated_at ? ` · updated ${new Date(status.updated_at).toLocaleString()}` : ''}
                        </span>
                    ) : (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                            Not set
                        </span>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-1">
                    <Input
                        label={status?.is_set ? 'New master password' : 'Master password'}
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        required
                        rightElement={
                            <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => setShowPassword((v) => !v)}
                                className="text-[var(--subtitle-color)] hover:text-[var(--secondary-color)] cursor-pointer"
                            >
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        }
                    />
                    <Input
                        label="Confirm password"
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter the same password"
                        required
                    />

                    <div className="flex items-center gap-2 pt-2">
                        <Button type="submit" isLoading={setMutation.isPending} disabled={setMutation.isPending}>
                            {status?.is_set ? 'Update master password' : 'Set master password'}
                        </Button>
                        {status?.is_set && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleClear}
                                isLoading={clearMutation.isPending}
                                disabled={clearMutation.isPending}
                            >
                                <Trash2 size={14} className="mr-1.5" />
                                Clear
                            </Button>
                        )}
                    </div>
                </form>
            </div>

            {status?.is_set && (
                <div className="bg-[var(--white-color)] rounded-2xl border border-[var(--neutral-color)] p-4 sm:p-6 max-w-2xl">
                    <div className="flex items-center gap-2 mb-3">
                        <Clock size={16} className="text-[var(--subtitle-color)]" />
                        <h2 className="text-sm font-semibold text-[var(--secondary-color)]">
                            Recent master-password logins
                        </h2>
                    </div>
                    {usageLog.length === 0 ? (
                        <p className="text-xs text-[var(--subtitle-color)]">
                            No student has been logged into via the master password yet.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-left text-xs">
                                <thead className="border-b border-[var(--neutral-color)]">
                                    <tr>
                                        <th className="py-2 pr-4 font-semibold text-[var(--subtitle-color)]">Enrolment No.</th>
                                        <th className="py-2 pr-4 font-semibold text-[var(--subtitle-color)]">When</th>
                                        <th className="py-2 pr-4 font-semibold text-[var(--subtitle-color)]">IP address</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[var(--neutral-color)]">
                                    {usageLog.map((row) => (
                                        <tr key={row.id}>
                                            <td className="py-2 pr-4 font-semibold text-[var(--secondary-color)]">
                                                {row.enrollment_number}
                                            </td>
                                            <td className="py-2 pr-4 text-[var(--subtitle-color)]">
                                                {new Date(row.created_at).toLocaleString()}
                                            </td>
                                            <td className="py-2 pr-4 text-[var(--subtitle-color)]">
                                                {row.ip_address || '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MasterPasswordPage;
